import { VertexLabel } from "./VertexLabel";

// The radar itself. Rings + spokes + target/current polygons rendered in SVG,
// with an HTML overlay of <VertexLabel> hit zones. Two modes:
//   showLabels=true  — full Vertex+Label (used at rest / drilled main arena)
//   showLabels=false — pip-only hit zones (used in the mini receptor strip)
//
// Value convention (Phase 3, matches N-Block dashboard):
//   Receptor profile values are negative — more-negative means stronger
//   response. The radar mirrors Plotly's `autorange: 'reversed'` in
//   build_dashboard.py:3134: the most-negative observed value lives at the
//   outer edge, 0 lives at the center. Positives are clamped at the center.

type SR3PlotProps = {
  axes: string[];
  target: number[];
  current: number[];
  size: number;
  /** Highlighted vertex (the user has picked it). */
  selectedIdx?: number | null;
  /** Armed receptor (in main receptor view). */
  activeIdx?: number | null;
  hovered?: number | null;
  showLabels?: boolean;
  onHover?: (i: number | null) => void;
  onVertexClick?: (i: number) => void;
  /** Override for the Current polygon's fill + stroke. Used by the AF
   *  Review state to render the proposed Current series in orange. When
   *  unset, falls back to `--accent` / `--accent-ink`. */
  currentColor?: string;
  currentColorInk?: string;
};

export function SR3Plot({
  axes,
  target,
  current,
  size,
  selectedIdx = null,
  activeIdx = null,
  hovered = null,
  showLabels = true,
  onHover,
  onVertexClick,
  currentColor,
  currentColorInk,
}: SR3PlotProps) {
  const n = axes.length;
  const cx = size / 2;
  const cy = size / 2;
  // Headroom for labels above each vertex (label height + gap + pip radius).
  const r = size / 2 - Math.max(28, Math.round(size * 0.12));

  // Effective most-negative value across both polygons. Forced ≤ -EPS so the
  // denominator is never 0 (degenerate all-zero case → polygon collapses to
  // center, which is the right visual).
  const EPS = 1e-9;
  const allVals = [...target, ...current];
  const observedMin = allVals.length === 0 ? 0 : Math.min(...allVals);
  const effectiveMin = Math.min(-EPS, observedMin);

  const angleFor = (i: number) => (i / n) * Math.PI * 2 - Math.PI / 2;
  const pointAt = (i: number, v: number): [number, number] => {
    const a = angleFor(i);
    // autorange-reversed semantics: 0 at center, effectiveMin (most negative)
    // at the edge. Positives clamp at 0 (center).
    const ratio = v >= 0 ? 0 : Math.max(0, Math.min(1, v / effectiveMin));
    const rr = ratio * r;
    return [cx + Math.cos(a) * rr, cy + Math.sin(a) * rr];
  };
  const polyPoints = (values: number[]) =>
    values.map((v, i) => pointAt(i, v).join(",")).join(" ");

  const rings = [0.25, 0.5, 0.75, 1.0];

  // Scale label font with radar size, clamped for legibility.
  const baseFont = size < 220 ? 8.5 : size < 320 ? 9.5 : 10;
  const fontSize = n > 12 ? baseFont - 1 : baseFont;

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
      }}
    >
      <svg width={size} height={size} style={{ display: "block" }}>
        {rings.map((f, idx) => (
          <polygon
            key={`r${idx}`}
            points={axes
              .map((_, i) => {
                const a = angleFor(i);
                return [cx + Math.cos(a) * r * f, cy + Math.sin(a) * r * f].join(",");
              })
              .join(" ")}
            fill={idx === rings.length - 1 ? "var(--bg-sunk)" : "none"}
            stroke="var(--line)"
            strokeWidth="0.5"
            strokeDasharray={idx === rings.length - 1 ? undefined : "2 2"}
          />
        ))}
        {axes.map((_, i) => {
          // Spoke endpoint is the outer edge of the radar — distance r from
          // center along the axis angle. Independent of data values.
          const a = angleFor(i);
          const x = cx + Math.cos(a) * r;
          const y = cy + Math.sin(a) * r;
          const isHi = selectedIdx === i || hovered === i || activeIdx === i;
          return (
            <line
              key={`sp${i}`}
              x1={cx}
              y1={cy}
              x2={x}
              y2={y}
              stroke={isHi ? "var(--accent-ink)" : "var(--line)"}
              strokeWidth={isHi ? 0.9 : 0.5}
              strokeOpacity={isHi ? 0.55 : 1}
            />
          );
        })}

        {/* Target — dashed outline */}
        <polygon
          points={polyPoints(target)}
          fill="var(--ink)"
          fillOpacity="0.04"
          stroke="var(--ink)"
          strokeWidth="1.25"
          strokeDasharray="4 3"
        />
        {target.map((v, i) => {
          const [x, y] = pointAt(i, v);
          return (
            <circle
              key={`tg${i}`}
              cx={x}
              cy={y}
              r="2.2"
              fill="var(--panel)"
              stroke="var(--ink)"
              strokeWidth="1"
            />
          );
        })}

        {/* Current — solid accent, or override color (AF Review). */}
        <polygon
          points={polyPoints(current)}
          fill={currentColor ?? "var(--accent)"}
          fillOpacity="0.32"
          stroke={currentColorInk ?? "var(--accent-ink)"}
          strokeWidth="1.6"
        />
        {current.map((v, i) => {
          const [x, y] = pointAt(i, v);
          const big = hovered === i || selectedIdx === i || activeIdx === i;
          return (
            <circle
              key={`cd${i}`}
              cx={x}
              cy={y}
              r={big ? 3.4 : 2.6}
              fill={currentColorInk ?? "var(--accent-ink)"}
            />
          );
        })}
      </svg>

      {/* HTML vertex + label layer */}
      {showLabels &&
        axes.map((axis, i) => {
          const a = angleFor(i);
          const vx = cx + Math.cos(a) * r;
          const vy = cy + Math.sin(a) * r;
          return (
            <VertexLabel
              key={`vl${i}`}
              axis={axis}
              vx={vx}
              vy={vy}
              fontSize={fontSize}
              isHover={hovered === i}
              isSel={selectedIdx === i}
              isActive={activeIdx === i}
              onHover={() => onHover?.(i)}
              onLeave={() => onHover?.(null)}
              onClick={onVertexClick ? () => onVertexClick(i) : undefined}
            />
          );
        })}
      {/* Pip-only hit zones for the mini strip (labels hidden) */}
      {!showLabels &&
        axes.map((axis, i) => {
          const a = angleFor(i);
          const vx = cx + Math.cos(a) * r;
          const vy = cy + Math.sin(a) * r;
          const isHover = hovered === i;
          const isSel = selectedIdx === i;
          const isActive = activeIdx === i;
          const accent = isSel || isActive;
          const pipSize = isHover || accent ? 10 : 8;
          return (
            <div
              key={`vp${i}`}
              onMouseEnter={() => onHover?.(i)}
              onMouseLeave={() => onHover?.(null)}
              onClick={(e) => {
                e.stopPropagation();
                onVertexClick?.(i);
              }}
              title={axis}
              className="sr3-vertex"
              style={{
                position: "absolute",
                left: vx,
                top: vy,
                transform: "translate(-50%, -50%)",
                cursor: onVertexClick ? "pointer" : "default",
                padding: 3,
                zIndex: isHover ? 30 : accent ? 12 : 2,
              }}
            >
              <div
                style={{
                  width: pipSize,
                  height: pipSize,
                  borderRadius: "50%",
                  background: isSel
                    ? "var(--accent)"
                    : isActive
                    ? "var(--accent-soft)"
                    : "var(--panel)",
                  border: `${isSel ? 2 : isActive ? 1.8 : 1}px ${
                    isSel || isActive || isHover ? "solid" : "dashed"
                  } ${accent || isHover ? "var(--accent-ink)" : "var(--line-strong)"}`,
                  boxSizing: "border-box",
                }}
              />
            </div>
          );
        })}
    </div>
  );
}
